import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Card,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Logout,
  AttachMoney,
  CloudUpload,
  Delete,
  Send,
  ArrowBack,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { createRequest } from '../services/dataServiceWrapper';
import { uploadFiles } from '../services/fileService';
import { getGraphClient } from '../services/graphService';
import { sendApproverNotification, sendSubmitterConfirmation } from '../services/emailService';
import { createNotification, NotificationType } from '../services/notificationService';

// MR_Approvers group ID (same as Marketing Request)
const MR_APPROVERS_GROUP_ID = 'e4806c56-b055-478c-ae42-0c19a93b88ed';

const schema = yup.object({
  expenseDescription: yup.string().required('Expense description is required'),
  cost: yup.number()
    .typeError('Cost must be a number')
    .positive('Cost must be positive')
    .required('Cost is required'),
  approver: yup.string().email('Invalid email').required('Approver is required'),
  accountName: yup.string(),
  bsb: yup.string()
    .test('valid-bsb', 'BSB must be 6 digits', function(value) {
      // Only validate if value is provided
      if (!value) return true;
      return /^\d{6}$/.test(value);
    }),
  accountNumber: yup.string()
    .test('valid-account', 'Account number must be 6-10 digits', function(value) {
      // Only validate if value is provided
      if (!value) return true;
      return /^\d{6,10}$/.test(value);
    }),
});

const ExpenseReimbursement = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      expenseDescription: '',
      cost: '',
      approver: '',
      accountName: '',
      bsb: '',
      accountNumber: '',
    },
  });

  useEffect(() => {
    loadApprovers();
  }, [account]);

  const loadApprovers = async () => {
    try {
      const graphClient = getGraphClient(instance, account);

      // Get members of MR_Approvers group
      const response = await graphClient
        .api(`/groups/${MR_APPROVERS_GROUP_ID}/members`)
        .select('id,displayName,mail,userPrincipalName')
        .top(999)
        .get();

      const approverList = response.value
        .filter(member => member['@odata.type'] === '#microsoft.graph.user')
        .map(member => ({
          id: member.id,
          name: member.displayName || member.userPrincipalName,
          email: member.mail || member.userPrincipalName,
        }));

      setApprovers(approverList);
      console.log('Loaded MR_Approvers for expense request:', approverList);
    } catch (error) {
      console.error('Error loading approvers:', error);
      toast.error('Failed to load approvers from Azure AD');
    } finally {
      setLoadingApprovers(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc', '.docx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    if (files.length === 0) {
      toast.error('Please attach at least one receipt or invoice');
      return;
    }

    setLoading(true);
    try {
      const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
      const userName = account?.name || userEmail.split('@')[0];
      const selectedApprover = approvers.find(a => a.email === data.approver);

      const expenseRequest = {
        id: Date.now().toString(),
        type: 'reimbursement',
        title: `Expense Reimbursement - $${data.cost}`,
        submittedBy: userEmail,
        submitterName: userName,
        submittedDate: new Date().toISOString(),
        status: 'pending', // Now requires approval
        approver: data.approver,
        approverName: selectedApprover?.name || data.approver,

        // Expense-specific fields
        expenseDescription: data.expenseDescription,
        cost: parseFloat(data.cost),
        accountName: data.accountName,
        bsb: data.bsb,
        accountNumber: data.accountNumber,

        // File attachments - empty, will upload separately
        documents: [],
      };

      const createdRequest = await createRequest(expenseRequest);

      // Upload files (creates entries in request_documents table)
      if (files.length > 0) {
        try {
          await uploadFiles(createdRequest.id, files, userEmail);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue anyway
        }
      }

      // Send approval notification to selected approver
      await sendApproverNotification(expenseRequest, data.approver);

      // Send confirmation email to submitter
      await sendSubmitterConfirmation(expenseRequest);

      // Create notification for approver
      createNotification({
        type: NotificationType.APPROVAL_NEEDED,
        userEmail: data.approver,
        recipientEmail: data.approver,
        title: 'New Expense Reimbursement',
        message: `${userName} has submitted an expense reimbursement for $${data.cost}`,
        requestId: expenseRequest.id
      });

      toast.success('Expense reimbursement submitted successfully!');
      navigate('/my-requests');
    } catch (error) {
      console.error('Error submitting expense reimbursement:', error);
      toast.error('Failed to submit expense reimbursement');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Button
            color="inherit"
            startIcon={<AttachMoney />}
            onClick={() => navigate('/my-requests')}
            sx={{ mr: 2 }}
          >
            My Requests
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {account?.name || account?.username}
            </Typography>
            <Avatar sx={{ bgcolor: '#d32f2f' }}>
              {(account?.name || account?.username || 'U')[0].toUpperCase()}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#0078d4', fontWeight: 'bold', mb: 3, textTransform: 'uppercase' }}>
            Expense Reimbursement Form
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Hello! We understand managing your business-related expenses is important and we want to ensure you're reimbursed promptly and accurately. 
            This Expense Reimbursement Form is designed to streamline that process for you. Please fill in the necessary details about your expenditures, and 
            remember to attach all relevant receipts or invoices. Your submission will be reviewed in accordance with ACOM's policy, and we will process your 
            reimbursement as quickly as possible. If there are any questions or additional information needed, the finance department will reach out to you. 
            Thank you for your diligence in managing company resources!
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Row - Expense Description, Cost, and Approver */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expense Description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    {...register('expenseDescription')}
                    error={!!errors.expenseDescription}
                    helperText={errors.expenseDescription?.message}
                    placeholder="Describe the expense..."
                  />
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Cost
                  </Typography>
                  <TextField
                    fullWidth
                    {...register('cost')}
                    error={!!errors.cost}
                    helperText={errors.cost?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    placeholder="0.00"
                  />
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Approver
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    {...register('approver')}
                    error={!!errors.approver}
                    helperText={errors.approver?.message || (loadingApprovers ? 'Loading...' : 'Required')}
                    disabled={loadingApprovers}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select approver</option>
                    {approvers.map((approver) => (
                      <option key={approver.email} value={approver.email}>
                        {approver.name}
                      </option>
                    ))}
                  </TextField>
                </Card>
              </Box>

              {/* Second Row - Account Details */}
              <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  Account Details
                </Typography>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Optional - The reimbursement will be processed to the account on file if left blank.
                </Alert>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    {...register('accountName')}
                    error={!!errors.accountName}
                    helperText={errors.accountName?.message}
                    placeholder="Account Name"
                  />
                  <TextField
                    fullWidth
                    label="BSB"
                    {...register('bsb')}
                    error={!!errors.bsb}
                    helperText={errors.bsb?.message}
                    placeholder="123456"
                  />
                  <TextField
                    fullWidth
                    label="Account Number"
                    {...register('accountNumber')}
                    error={!!errors.accountNumber}
                    helperText={errors.accountNumber?.message}
                    placeholder="1234567890"
                  />
                </Box>
              </Card>

              {/* Upload Files */}
              <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload Files
                </Typography>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <input {...getInputProps()} />
                  {files.length === 0 ? (
                    <>
                      <Typography>There is nothing attached.</Typography>
                      <Button
                        startIcon={<CloudUpload />}
                        sx={{ mt: 1 }}
                        variant="text"
                      >
                        Attach file
                      </Button>
                    </>
                  ) : (
                    <Box>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">{file.name}</Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {files.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please attach receipts or invoices for your expense claim
                  </Alert>
                )}
              </Card>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={{
                    px: 6,
                    py: 1.5,
                    bgcolor: '#0078d4',
                    '&:hover': {
                      bgcolor: '#106ebe',
                    },
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2024 ACOM Apps Portal. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default ExpenseReimbursement;